import React, { useState, useEffect, useMemo } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Spinner,
  Row,
  Col,
} from "reactstrap";
import Select from "react-select";
import toast from "react-hot-toast";
import ResponsiveDataManagement from "../../../Components/Common/ResponsiveManagementLayout";
import TableSearch from "../../../Components/Common/TableSearch";
import PaymentTypeLedgerService from "../../../services/AccountingServices/PaymentTypeLedgerService";
import PaymentTypeServices from "../../../services/OrderingServices/PaymentTypeService";
import LedgersService from "../../../services/AccountingServices/LedgerService";
import useIsSmallScreen from "../../Payment Type/Component/small screen/SmallScreen";
import { Card, CardBody, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";

const PaymentTypeLedgerSettings = () => {
  const [loading, setLoading] = useState(true);
  const [mappings, setMappings] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [formData, setFormData] = useState({
    paymentTypeId: "",
    ledgerId: "",
    description: "",
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [mappingsData, ptData, ledgersResponse] = await Promise.all([
        PaymentTypeLedgerService.getList(),
        PaymentTypeServices.getList(),
        LedgersService.getList({ pageSize: 1000 }),
      ]);

      setMappings(mappingsData || []);
      
      const pts = Array.isArray(ptData) ? ptData : ptData?.items || [];
      setPaymentTypes(pts.map(pt => ({ value: pt.id, label: `${pt.name} (${pt.code})` })));

      const ledgerList = Array.isArray(ledgersResponse) ? ledgersResponse : ledgersResponse?.items || [];
      setLedgers(ledgerList.map(l => ({ value: l.id, label: `${l.name} (${l.code})` })));
    } catch (error) {
      console.error("Error fetching mapping data:", error);
      toast.error("Failed to load mapping data");
    } finally {
      setLoading(false);
    }
  };

  const toggleModal = (mapping = null) => {
    if (mapping) {
      setSelectedMapping(mapping);
      setFormData({
        paymentTypeId: mapping.paymentTypeId,
        ledgerId: mapping.ledgerId,
        description: mapping.description || "",
      });
    } else {
      setSelectedMapping(null);
      setFormData({
        paymentTypeId: "",
        ledgerId: "",
        description: "",
      });
    }
    setIsModalOpen(!isModalOpen);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, selectedOption) => {
    setFormData(prev => ({ ...prev, [name]: selectedOption ? selectedOption.value : "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setUpdating(true);
      if (selectedMapping) {
        await PaymentTypeLedgerService.update(selectedMapping.id, {
          ledgerId: formData.ledgerId,
          description: formData.description
        });
        toast.success("Mapping updated successfully");
      } else {
        await PaymentTypeLedgerService.create(formData);
        toast.success("Mapping created successfully");
      }
      fetchInitialData();
      toggleModal();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save mapping");
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async (mapping) => {
    try {
      await PaymentTypeLedgerService.delete(mapping.id);
      toast.success("Mapping deleted successfully");
      fetchInitialData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete mapping");
    }
  };

  const filteredMappings = useMemo(() => {
    if (!searchTerm) return mappings;
    const lowerSearch = searchTerm.toLowerCase();
    return mappings.filter(m => 
      m.paymentTypeName?.toLowerCase().includes(lowerSearch) ||
      m.paymentTypeCode?.toLowerCase().includes(lowerSearch) ||
      m.ledgerName?.toLowerCase().includes(lowerSearch) ||
      m.ledgerCode?.toLowerCase().includes(lowerSearch)
    );
  }, [mappings, searchTerm]);

  const columns = [
    {
      name: "S.N",
      selector: (row, index) => index + 1,
      width: "70px",
    },
    {
      name: "Payment Type",
      selector: (row) => row.paymentTypeName,
      sortable: true,
      cell: (row) => (
        <div>
          <span className="fw-bold">{row.paymentTypeName}</span>
          <br />
          <code className="text-muted small">{row.paymentTypeCode}</code>
        </div>
      ),
    },
    {
      name: "Mapped Ledger",
      selector: (row) => row.ledgerName,
      sortable: true,
      cell: (row) => (
        <div>
          <span className="text-primary fw-medium">{row.ledgerName}</span>
          <br />
          <span className="text-muted small">Code: {row.ledgerCode}</span>
        </div>
      ),
    },
    {
      name: "Description",
      selector: (row) => row.description,
      wrap: true,
      cell: (row) => <span className="">{row.description || "-"}</span>,
    }
  ];

  return (
    <div className="page-content p-0">
      <ResponsiveDataManagement
        title="Payment Type Ledgers"
        pageTitle="Accounting Setup"
        mobileCardTitle="Mapping List"
        data={filteredMappings}
        columns={columns}
        onAdd={() => toggleModal()}
        onEdit={toggleModal}
        onDelete={handleDelete}
        loading={loading}
        searchKeys={["paymentTypeName", "paymentTypeCode", "ledgerName", "ledgerCode"]}
        searchTerm={searchTerm}
        onSearch={setSearchTerm}
        emptyStateMessage="No payment type mappings found"
        mobileCardRenderer={(item) => (
          <Card className="shadow-sm rounded-3 border-0 border-start border-4 border-primary mb-3">
            <CardBody className="p-3">
              <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                  <div className="mb-2">
                    <span className="small text-muted text-uppercase fw-bold">Payment Type</span>
                    <h6 className="fw-bold text-dark mb-0">{item.paymentTypeName}</h6>
                    <code className="text-muted small">{item.paymentTypeCode}</code>
                  </div>
                  
                  <div className="mb-2">
                    <span className="small text-muted text-uppercase fw-bold">Mapped Ledger</span>
                    <h6 className="fw-medium text-primary mb-0">{item.ledgerName}</h6>
                    <span className="text-muted small">Code: {item.ledgerCode}</span>
                  </div>

                  {item.description && (
                    <div className="mt-2 text-muted small border-top pt-2">
                      <i className="ri-information-line me-1"></i>
                      {item.description}
                    </div>
                  )}
                </div>

                <Dropdown
                  isOpen={openDropdownId === item.id}
                  toggle={() => setOpenDropdownId(openDropdownId === item.id ? null : item.id)}
                >
                  <DropdownToggle color="light" size="sm" className="rounded-circle shadow-sm">
                    <i className="ri-more-2-fill" />
                  </DropdownToggle>
                  <DropdownMenu end>
                    <DropdownItem onClick={() => toggleModal(item)}>
                      <i className="ri-edit-line me-2 align-middle text-primary" /> Edit
                    </DropdownItem>
                    <DropdownItem onClick={() => handleDelete(item)}>
                      <i className="ri-delete-bin-line me-2 align-middle text-danger" /> Delete
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </div>
            </CardBody>
          </Card>
        )}
      />

      <Modal isOpen={isModalOpen} toggle={() => toggleModal()} size="lg" centered>
        <ModalHeader toggle={() => toggleModal()} className="pb-2 border-bottom-0 text-dark">
          <span className="fw-medium">{selectedMapping ? "Edit Mapping" : "New Mapping"}</span>
        </ModalHeader>
        <Form onSubmit={handleSubmit}>
          <ModalBody className="py-2">
            <Row className="g-3">
              <Col md={6}>
                <FormGroup className="mb-2">
                  <Label className="fw-semibold small text-muted text-uppercase mb-1">Payment Type</Label>
                  <Select
                    options={paymentTypes}
                    value={paymentTypes.find(opt => opt.value === formData.paymentTypeId)}
                    onChange={(opt) => handleSelectChange("paymentTypeId", opt)}
                    placeholder="Select Payment Type..."
                    isDisabled={!!selectedMapping}
                    classNamePrefix="react-select"
                    required
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup className="mb-2">
                  <Label className="fw-semibold small text-muted text-uppercase mb-1">Ledger Account</Label>
                  <Select
                    options={ledgers}
                    value={ledgers.find(opt => opt.value === formData.ledgerId)}
                    onChange={(opt) => handleSelectChange("ledgerId", opt)}
                    placeholder="Select Ledger..."
                    classNamePrefix="react-select"
                    required
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row className="g-3 mt-1">
              <Col lg={12}>
                <FormGroup className="mb-0">
                  <Label className="fw-semibold small text-muted text-uppercase mb-1">Description</Label>
                  <Input
                    type="textarea"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="2"
                    placeholder="Optional details about this mapping..."
                  />
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter className="border-top-0 pt-0">
            <Button color="light" type="button" size="sm" onClick={() => toggleModal()}>Cancel</Button>
            <Button color="primary" type="submit" size="sm" disabled={updating || !formData.paymentTypeId || !formData.ledgerId}>
              {updating ? <Spinner size="sm" /> : (selectedMapping ? "Update Mapping" : "Create Mapping")}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  );
};

export default PaymentTypeLedgerSettings;
